import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  IngredientCsvTemplate,
  ProductCsvTemplate,
} from '../../../../types/csvTemplates'

import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { MatInputModule } from '@angular/material/input'
import {
  MatPaginatorModule,
  MatPaginatorIntl,
  MatPaginator,
} from '@angular/material/paginator'
import { MatSelectModule } from '@angular/material/select'
import { MatTableModule, MatTableDataSource } from '@angular/material/table'
import { Router, RouterModule } from '@angular/router'
import { PaginatorIntl } from '../../../intl/paginator-intl'
import { Dialog } from '@angular/cdk/dialog'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { firstValueFrom } from 'rxjs'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { ApiError } from '../../../../types/apiError'
import {
  ConfirmDialogData,
  ConfirmDialogComponent,
} from '../../../shared/confirm-dialog/confirm-dialog.component'
import { IngredientCsvTemplatesService } from '../../../../services/ingredientCsvTemplate.service'
import { IngredientFieldMappingSheetComponent } from './ingredient-field-mapping-sheet/ingredient-field-mapping-sheet.component'
import { CommonService } from '../../../../services/common.service'

@Component({
  selector: 'app-ingredient-template-list',
  imports: [
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    FormsModule,
    MatChipsModule,
    RouterModule,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
  ],
  templateUrl: './ingredient-template-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ingredient-template-list.component.scss',
})
export class IngredientTemplateListComponent implements OnInit {
  translationService = inject(TranslationService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private csvTemplateService = inject(IngredientCsvTemplatesService)
  private _bottomSheet = inject(MatBottomSheet)
  private router = inject(Router)
  private commonService = inject(CommonService)

  csvTemplates: Array<IngredientCsvTemplate> = []
  displayedColumns: Array<string> = ['name', 'actions']
  dataSource = new MatTableDataSource<IngredientCsvTemplate>([])

  @ViewChild(MatPaginator) set paginator(mp: MatPaginator) {
    this.dataSource.paginator = mp
  }

  async ngOnInit(): Promise<void> {
    await this.getResults()
  }

  /**
   * Retrieves templates for a company
   */
  async getResults() {
    try {
      this.spinnerService.startSpinner()
      const response = await firstValueFrom(this.csvTemplateService.getAll())
      this.csvTemplates = response
      this.dataSource.data = this.csvTemplates
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Opens bottom sheet to show csv field mappings
   */
  showFieldMappings(csvTemplate: ProductCsvTemplate) {
    this._bottomSheet.open(IngredientFieldMappingSheetComponent, {
      data: {
        csvTemplate: csvTemplate,
      },
    })
  }

  /**
   * Navigates user to edit template screen
   * @param {string} id The id of the template to edit
   */
  goToEdit(id: string) {
    this.router.navigate(['settings', 'ingredient-csv-templates', id, 'edit'])
  }

  /**
   * Opens a dialog to prompt the user to delete a csv template. On succesful deletion, removes the deleted element from the list
   * @param {string} id Id of the template
   * @param {string} name Name of the template
   */
  async delete(id: string, name: string) {
    try {
      const dialogData: ConfirmDialogData = {
        header: this.translationService.getConfirmActionHeaderText(),
        message: this.translationService.getDeleteTemplateMessageText(name),
        onCancel: () => {},
      }

      const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
        data: dialogData,
      })

      dialogRef.closed.subscribe(async (confirmed) => {
        if (confirmed) {
          this.spinnerService.startSpinner()
          await firstValueFrom(this.csvTemplateService.delete(id))
          this.spinnerService.stopSpinner()

          // we can then remove the deleted template from csvTemplates array and update the dataSource
          const index = this.csvTemplates.findIndex(
            (template) => template.id === id,
          )
          if (index !== -1) {
            this.csvTemplates.splice(index, 1)
            this.dataSource.data = [...this.csvTemplates]
          }

          this.dialogService.showDialog(
            this.dialog,
            this.translationService.getProductCSVTemplatesText(),
            this.translationService.getTemplateDeletedText(),
          )
        }
      })
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Downloads a csv template
   * @param {string} id Id of the template
   * @param {string} name Name of the template
   */
  async download(id: string, name: string) {
    try {
      const fileName = name + '.csv'
      this.spinnerService.startSpinner()
      const base64 = await firstValueFrom(this.csvTemplateService.download(id))
      this.commonService.downloadBase64File(base64, fileName)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
